import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { processSteps, earlyQuestions, designStudios, approachEnterprises, approachFunding, approachSuccess } from "@/content/site";

const PROCESS_IMAGE = {
  src: "https://images.unsplash.com/photo-1590682680695-43b964a3ae17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxoYW5kcyUyMHBsYW50aW5nJTIwc2VlZHMlMjBkaXJ0fGVufDB8fHx8MTc5MjEwOTkyMnww&ixlib=rb-4.1.0&q=85&w=1400",
  alt: "Hands covered in dark soil planting a small green seedling in the ground",
};

export default function Approach() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <>
      <Seo
        title="Land Project Feasibility & Planning | Bioregional Ventures"
        description="A careful process for community land and cooperative projects: understand place, test feasibility, agree governance, match funding to the work, begin at a workable scale."
      />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">Approach</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight text-charcoal max-w-3xl" data-testid="approach-heading">
            Listen deeply. Think clearly. Build with purpose.
          </h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
            Every project starts with people and place. Using our approach, we test ideas for long-term sustainability, clarify who does what and establish solid foundations before moving forward.
          </p>
        </Reveal>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal max-w-2xl mb-10 md:mb-14" data-testid="approach-pathway-heading">
            From local priorities to practical delivery
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7">
            <ol className="space-y-0 divide-y divide-charcoal/10 border-y border-charcoal/10" data-testid="process-steps-list">
              {processSteps.map((step, i) => (
                <li key={step.number}>
                  <Reveal delay={i * 0.04}>
                    <div className="py-9 md:py-11 grid grid-cols-[3rem_1fr] gap-5" data-testid={`process-step-${step.number}`}>
                      <span className="font-serif text-ochre text-2xl leading-none pt-1">{step.number}</span>
                      <div>
                        <h2 className="font-serif text-xl md:text-2xl leading-snug text-charcoal">{step.title}</h2>
                        <p className="mt-3 text-base leading-relaxed text-charcoal/85">{step.text}</p>
                      </div>
                    </div>
                  </Reveal>
                </li>
              ))}
            </ol>
            <Reveal>
              <p className="mt-8 text-sm text-charcoal/60 italic" data-testid="approach-scoped-note">
                Each engagement is scoped to the project's stage, priorities and resources.
              </p>
            </Reveal>
          </div>
          <Reveal className="md:col-span-5" delay={0.1}>
            <div className="md:sticky md:top-28">
              <div className="overflow-hidden rounded-sm">
                <img
                  src={PROCESS_IMAGE.src}
                  alt={PROCESS_IMAGE.alt}
                  data-testid="approach-image"
                  loading="lazy"
                  className="w-full h-72 md:h-[480px] object-cover hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <p className="mt-4 text-xs text-charcoal/60 italic">
                Illustrative image: hands planting a seedling.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bioregional Design Studios */}
      <section id="design-studios" className="scroll-mt-24 border-t border-charcoal/10" data-testid="design-studios-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="ds-heading">
              {designStudios.heading}
            </h2>
            <p className="mt-3 font-serif italic text-xl md:text-2xl text-ochre-dark" data-testid="ds-standfirst">
              {designStudios.standfirst}
            </p>
            {designStudios.intro.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
          </Reveal>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <Reveal>
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="ds-explore-heading">
              {designStudios.exploreHeading}
            </h3>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0 divide-y md:divide-y-0 divide-charcoal/10">
            {designStudios.explore.map((item, i) => (
              <div key={item.title} className="py-5 md:border-b md:border-charcoal/10 flex gap-4" data-testid={`ds-explore-${i + 1}`}>
                <span className="font-serif text-ochre shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <p className="font-medium text-charcoal">{item.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-charcoal/85">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-sage/60 border-y border-charcoal/10">
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
            <Reveal>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="ds-outputs-heading">
                {designStudios.outputsHeading}
              </h3>
              <p className="mt-4 text-base md:text-lg leading-relaxed text-charcoal/90">
                {designStudios.outputsIntro}
              </p>
              <ul className="mt-6 space-y-2.5 text-base leading-relaxed text-charcoal/90 list-disc pl-5" data-testid="ds-outputs-list">
                {designStudios.outputs.map((o) => (
                  <li key={o}>{o}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <Reveal>
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="ds-trust-heading">
              {designStudios.trustHeading}
            </h3>
            {designStudios.trustBody.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
          </Reveal>
          <Reveal className="mt-12">
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="ds-abundance-heading">
              {designStudios.abundanceHeading}
            </h3>
            <div className="mt-5" data-testid="ds-abundance">
              {designStudios.abundanceParas.map((p) => (
                <p key={p.slice(0, 24)} className="mt-5 first:mt-0 text-base leading-relaxed text-charcoal/90">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal className="mt-12">
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="ds-flexible-heading">
              {designStudios.flexibleHeading}
            </h3>
            {designStudios.flexibleBody && (
              <p className="mt-5 text-base leading-relaxed text-charcoal/90">
                {designStudios.flexibleBody}
              </p>
            )}
            <p className="mt-6 text-xs text-charcoal/60 italic border-l-2 border-ochre pl-4" data-testid="ds-funding-note">
              {designStudios.fundingNote}
            </p>
            <Link
              to={designStudios.ctaHref}
              data-testid="ds-cta-button"
              className="mt-8 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
            >
              {designStudios.ctaLabel}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Enterprises and shared assets */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="approach-enterprises-heading">
              {approachEnterprises.heading}
            </h2>
            {approachEnterprises.body.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="approach-enterprises-grouping">
          {approachEnterprises.grouping.map((g, i) => (
            <div key={g.title} className="border border-charcoal/15 bg-cream p-6" data-testid={`approach-grouping-${i + 1}`}>
              <p className="font-medium text-charcoal">{g.title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-charcoal/85">{g.text}</p>
            </div>
          ))}
        </div>
        <Reveal>
          <p className="mt-4 text-xs text-charcoal/60 italic">{approachEnterprises.groupingNote}</p>
        </Reveal>
      </section>

      {/* Funding pathways */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <p className="text-base md:text-lg leading-relaxed text-charcoal/90" data-testid="approach-funding-crosslink">
              {approachFunding.text}{" "}
              <Link
                to={approachFunding.href}
                data-testid="approach-funding-link"
                className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
              >
                {approachFunding.linkText}
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* What success means */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="approach-success-heading">
            {approachSuccess.heading}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">{approachSuccess.intro}</p>
          <ul className="mt-6 space-y-2.5 text-base md:text-lg leading-relaxed text-charcoal/90 list-disc pl-5" data-testid="approach-success-questions">
            {approachSuccess.questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
          <p className="mt-6 text-base leading-relaxed text-charcoal/90">{approachSuccess.outro}</p>
        </Reveal>
      </section>

      <section id="early-questions" className="bg-sage/60 border-y border-charcoal/10 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Reveal>
            <div className="max-w-3xl" data-testid="early-questions-panel">
              <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">Before we begin</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal">
                Questions we ask early
              </h2>
              <ul className="mt-10 space-y-0 divide-y divide-charcoal/10 border-y border-charcoal/20">
                {earlyQuestions.map((q, i) => (
                  <li key={q} className="py-5 flex gap-5 items-baseline" data-testid={`early-question-${i + 1}`}>
                    <span className="font-serif text-ochre shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-base md:text-lg text-charcoal/90">{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
        <Reveal>
          <p className="font-serif text-2xl md:text-3xl leading-snug text-charcoal max-w-xl mx-auto">
            Sound like the right way to work?
          </p>
          <Link
            to="/contact"
            data-testid="approach-cta-button"
            className="mt-8 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            Start a conversation
          </Link>
        </Reveal>
      </section>
    </>
  );
}
